import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import { Button, GridItem, Typography } from "@insite/mobius/index";
import LoadingSpinner, { LoadingSpinnerProps } from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import getPackagingSlipPrintHandler from "@tKgpco/Handlers/CustomPackagingSlipPrintHandler";
import { PackagingSlip, PoLinesShippedWithQuantity } from "@tKgpco/Services/CustomPackagingSlipsService";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface Props {
    item: PackagingSlip;
}

type ComponentProps = Props & ResolveThunks<typeof mapDispatchToProps>;

export interface ProductSearchScoreDataStyles {
    container?: GridContainerProps;
    dateGridItem?: GridItemProps;
    poLinesGridItem?: GridItemProps;
    buttonGridItem?: GridItemProps;
    poLineKey?: InjectableCss;
    poLineValue?: InjectableCss;
    button?: ButtonPresentationProps;
    loadingSpinner?: LoadingSpinnerProps;
}

export const packagingSlipsStyles: ProductSearchScoreDataStyles = {
    container: {
        css: css`
            flex-direction: column;
        `,
    },
    dateGridItem: {
        width: [4, 2, 2, 2, 2],
        css: css``,
    },
    poLinesGridItem: {
        width: [8, 8, 8, 8, 8],
        css: css`
            display: flex;
            flex-direction: row;
            flex-wrap: wrap;
        `,
    },
    buttonGridItem: {
        width: [12, 2, 2, 2, 2],
        css: css`
            justify-content: right;
        `,
    },

    poLineKey: {
        css: css`
            font-weight: 600;
        `,
    },
    poLineValue: {},
    button: {
        buttonType: "outline",
        variant: "secondary",
        mergeCss: true,
        css: css`
            [class^="IconWrapper"] {
                color: ${getColor("primary")};
            }
        `,
    },
};

const mapDispatchToProps = {
    getPackagingSlipPrintHandler,
};

const styles = packagingSlipsStyles;

const PackagingSlip: FC<ComponentProps> = ({ item, getPackagingSlipPrintHandler }) => {
    const [isButtonLoading, setIsButtonLoading] = React.useState(false);
    const toasterContext = useContext(ToasterContext);

    const handlePrint = (packagingSlipId: string) => {
        setIsButtonLoading(true);
        getPackagingSlipPrintHandler({
            packagingSlipId,
            onSuccess: () => {
                setIsButtonLoading(false);
                toasterContext.addToast({
                    body: translate("Please click on the print button again if the pdf does not open."),
                    messageType: "warning",
                });
            },
            onError: () => {
                setIsButtonLoading(false);
            },
        });
    };

    return (
        <GridContainer key={item.packagingSlipId} {...styles.container}>
            <GridItem {...styles.dateGridItem}>{item.packagingSlipDate}</GridItem>
            <GridItem {...styles.poLinesGridItem}>
                {item.poLinesShippedWithQuantity.map((poLine: PoLinesShippedWithQuantity, i: number) => {
                    return (
                        <StyledWrapper key={poLine.key}>
                            <Typography as="span" {...styles.poLineKey}>
                                {poLine.key}:&nbsp;
                            </Typography>
                            <Typography as="span" {...styles.poLineValue}>
                                {poLine.value}
                                {item.poLinesShippedWithQuantity.length - 1 !== i && ","}&nbsp;
                            </Typography>
                        </StyledWrapper>
                    );
                })}
            </GridItem>
            <GridItem {...styles.buttonGridItem}>
                <Button
                    {...styles.button}
                    icon={{ src: isButtonLoading ? LoadingSpinner : "", position: "right" }}
                    onClick={() => handlePrint(item.packagingSlipId)}
                >
                    {translate("Print")}
                </Button>
            </GridItem>
        </GridContainer>
    );
};

export default connect(null, mapDispatchToProps)(PackagingSlip);
